// Pestaña Abiertos: lo que tiene etiqueta y sigue abierto, ordenado por
// caducidad. Desde aquí se marca como gastado, consumido o retirado (con
// Deshacer durante 15 s), se reimprime y se congela o descongela.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Printer, RotateCcw, Snowflake, Trash2, CheckCircle2, Undo2 } from 'lucide-react';
import { etiquetasAPI, textoErrorApi } from '../../api/api';
import { toast } from '../../hooks/use-toast';
import { AVISO_SIMULACION } from '../../context/VistaEmpleadoContext';
import { prepararImpresion, imprimirEtiqueta, cancelarImpresion } from '../../utils/imprimirEtiqueta';
import { VERBO_ACCION, diaMes, horaDe, inicioApertura } from '../../utils/etiquetas';

const MS_DESHACER = 15000;

const FILTROS = [
  { valor: 'todos', texto: 'Todos' },
  { valor: 'caducados', texto: 'Caducados' },
  { valor: 'hoy', texto: 'Caducan hoy' },
];

const BARRA = {
  caducado: 'bg-red-500',
  hoy: 'bg-amber-500',
  pronto: 'bg-amber-400',
  ok: 'bg-emerald-500',
};

const PILDORA = {
  caducado: 'bg-red-50 text-red-700 border-red-200',
  hoy: 'bg-amber-50 text-amber-800 border-amber-200',
  pronto: 'bg-amber-50 text-amber-800 border-amber-200',
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const TEXTO_CIERRE = {
  gastada: 'gastado',
  consumida: 'consumido',
  retirada: 'retirado',
};

const claseBoton = 'h-11 inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition disabled:opacity-50 disabled:pointer-events-none';
const claseNeutro = `${claseBoton} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;

const errorDe = (e, porDefecto) => textoErrorApi(e?.response?.data?.detail, porDefecto);

const caducaCorta = (a) => {
  const h = a.caduca_con_hora ? horaDe(a.caduca) : '';
  return h ? `${diaMes(a.caduca)} ${h}` : diaMes(a.caduca);
};

const AbiertosTab = ({ aperturas, cargando, error, onReintentar, onRecargar, soloLectura }) => {
  const [filtro, setFiltro] = useState('todos');
  const [ocupado, setOcupado] = useState(null);
  const [deshacer, setDeshacer] = useState(null);
  const [deshaciendo, setDeshaciendo] = useState(false);

  useEffect(() => {
    if (!deshacer) return undefined;
    const t = setTimeout(() => setDeshacer(null), MS_DESHACER);
    return () => clearTimeout(t);
  }, [deshacer]);

  const cuentas = useMemo(() => ({
    todos: aperturas.length,
    caducados: aperturas.filter((a) => a.estado_caducidad === 'caducado').length,
    hoy: aperturas.filter((a) => a.estado_caducidad === 'hoy').length,
  }), [aperturas]);

  const visibles = useMemo(() => {
    if (filtro === 'caducados') return aperturas.filter((a) => a.estado_caducidad === 'caducado');
    if (filtro === 'hoy') return aperturas.filter((a) => a.estado_caducidad === 'hoy');
    return aperturas;
  }, [aperturas, filtro]);

  const cerrar = useCallback(async (apertura, estado) => {
    if (soloLectura) {
      toast({ title: AVISO_SIMULACION });
      return;
    }
    setOcupado(apertura.id);
    try {
      await etiquetasAPI.cambiarEstado(apertura.id, estado);
      setDeshacer({ id: apertura.id, nombre: apertura.nombre, estado });
      onRecargar();
    } catch (e) {
      toast({ title: 'No se pudo cerrar', description: errorDe(e, 'Inténtalo otra vez.'), variant: 'destructive' });
    } finally {
      setOcupado(null);
    }
  }, [soloLectura, onRecargar]);

  const deshacerCierre = useCallback(async () => {
    if (!deshacer) return;
    setDeshaciendo(true);
    try {
      await etiquetasAPI.cambiarEstado(deshacer.id, 'abierta');
      setDeshacer(null);
      onRecargar();
    } catch (e) {
      toast({ title: 'No se pudo deshacer', description: errorDe(e, 'Inténtalo otra vez.'), variant: 'destructive' });
    } finally {
      setDeshaciendo(false);
    }
  }, [deshacer, onRecargar]);

  const reimprimir = useCallback(async (apertura) => {
    const ventana = prepararImpresion();
    setOcupado(apertura.id);
    try {
      await imprimirEtiqueta(apertura, ventana);
    } catch (e) {
      cancelarImpresion(ventana);
      toast({ title: 'No se pudo imprimir', description: 'Inténtalo otra vez.', variant: 'destructive' });
    } finally {
      setOcupado(null);
    }
  }, []);

  // Congelar o descongelar: crea la etiqueta nueva (mismo lote) y la imprime
  const cambiarFrio = useCallback(async (apertura, congelar) => {
    if (soloLectura) {
      toast({ title: AVISO_SIMULACION });
      return;
    }
    // En el mismo clic, antes de esperar al servidor (iOS)
    const ventana = prepararImpresion();
    setOcupado(apertura.id);
    try {
      const { data: nueva } = congelar
        ? await etiquetasAPI.congelar(apertura.id)
        : await etiquetasAPI.descongelar(apertura.id);
      onRecargar();
      toast({
        title: congelar ? 'Etiqueta de congelado lista' : 'Etiqueta de descongelado lista',
        description: `Lote ${nueva?.lote || apertura.lote}. Cambia la etiqueta del recipiente.`,
      });
      try {
        await imprimirEtiqueta(nueva, ventana);
      } catch (e) {
        toast({ title: 'No se pudo abrir la impresión', description: 'Usa Reimprimir en la etiqueta nueva.', variant: 'destructive' });
      }
    } catch (e) {
      cancelarImpresion(ventana);
      toast({
        title: congelar ? 'No se pudo congelar' : 'No se pudo descongelar',
        description: errorDe(e, 'Inténtalo otra vez.'),
        variant: 'destructive',
      });
    } finally {
      setOcupado(null);
    }
  }, [soloLectura, onRecargar]);

  return (
    <div className="space-y-3 pb-20">
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1" role="tablist" aria-label="Filtrar">
        {FILTROS.map((f) => {
          const activo = filtro === f.valor;
          return (
            <button
              key={f.valor}
              type="button"
              role="tab"
              aria-selected={activo}
              onClick={() => setFiltro(f.valor)}
              className={`h-11 px-4 rounded-full border text-sm font-medium whitespace-nowrap transition ${activo
                ? 'bg-duetto-primary text-white border-duetto-primary'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              data-testid={`abiertos-filtro-${f.valor}`}
            >
              {f.texto} <span className={activo ? 'text-white/80' : 'text-gray-400'}>{cuentas[f.valor]}</span>
            </button>
          );
        })}
      </div>

      {cargando && !aperturas.length && (
        <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          Cargando...
        </div>
      )}

      {error && !aperturas.length && !cargando && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 space-y-2">
          <p>{error}</p>
          <button type="button" className={claseNeutro} onClick={onReintentar}>
            <RotateCcw className="w-4 h-4" />
            Reintentar
          </button>
        </div>
      )}

      {!cargando && !error && aperturas.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-600" data-testid="abiertos-vacio">
          No hay nada abierto. Cuando imprimas una etiqueta aparecerá aquí con su caducidad.
        </div>
      )}

      {aperturas.length > 0 && visibles.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-600">
          No hay ninguna en este filtro.
        </div>
      )}

      <ul className="space-y-2">
        {visibles.map((a) => {
          const estado = a.estado_caducidad || 'ok';
          const congelada = a.conservacion === 'congelado';
          const esPlato = a.tipo === 'plato';
          const trabajando = ocupado === a.id;
          return (
            <li key={a.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden flex" data-testid="abierto-tarjeta">
              <div className={`w-1.5 flex-shrink-0 ${BARRA[estado] || BARRA.ok}`} aria-hidden="true" />
              <div className="flex-1 min-w-0 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 leading-snug">{a.nombre}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Lote <span className="font-mono">{a.lote}</span>
                      {' · '}{VERBO_ACCION[a.accion] || 'Abierto'} {diaMes(inicioApertura(a))}
                      {' · '}caduca {caducaCorta(a)}
                    </p>
                    {(congelada || a.accion === 'descongelar') && (
                      <p className="mt-1 text-xs font-medium text-gray-700">
                        {congelada ? 'En el congelador' : 'Descongelado: no volver a congelar'}
                      </p>
                    )}
                  </div>
                  <span className={`flex-shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${PILDORA[estado] || PILDORA.ok}`}>
                    {a.texto_estado}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                  <button
                    type="button"
                    className={`${claseBoton} border-duetto-primary bg-duetto-primary text-white hover:bg-duetto-primary/90`}
                    onClick={() => cerrar(a, esPlato ? 'consumida' : 'gastada')}
                    disabled={trabajando}
                    data-testid="abierto-gastado"
                  >
                    {trabajando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {esPlato ? 'Consumido' : 'Gastado'}
                  </button>
                  <button
                    type="button"
                    className={`${claseBoton} ${estado === 'caducado'
                      ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
                    onClick={() => cerrar(a, 'retirada')}
                    disabled={trabajando}
                    data-testid="abierto-retirado"
                  >
                    <Trash2 className="w-4 h-4" />
                    Retirado
                  </button>
                  <button
                    type="button"
                    className={claseNeutro}
                    onClick={() => reimprimir(a)}
                    disabled={trabajando}
                    data-testid="abierto-reimprimir"
                  >
                    <Printer className="w-4 h-4" />
                    Reimprimir
                  </button>
                  {congelada ? (
                    <button
                      type="button"
                      className={claseNeutro}
                      onClick={() => cambiarFrio(a, false)}
                      disabled={trabajando}
                      data-testid="abierto-descongelar"
                    >
                      <Snowflake className="w-4 h-4" />
                      Descongelar
                    </button>
                  ) : a.accion !== 'descongelar' && (
                    <button
                      type="button"
                      className={claseNeutro}
                      onClick={() => cambiarFrio(a, true)}
                      disabled={trabajando}
                      data-testid="abierto-congelar"
                    >
                      <Snowflake className="w-4 h-4" />
                      Congelar
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {deshacer && (
        <div
          className="fixed bottom-4 inset-x-4 md:left-auto md:right-6 md:w-[26rem] z-40 rounded-xl bg-gray-900 text-white shadow-lg p-3 flex items-center gap-3"
          role="status"
          data-testid="abiertos-deshacer"
        >
          <p className="flex-1 min-w-0 text-sm">
            <span className="font-semibold">{deshacer.nombre}</span> marcado como {TEXTO_CIERRE[deshacer.estado] || 'cerrado'}.
          </p>
          <button
            type="button"
            onClick={deshacerCierre}
            disabled={deshaciendo}
            className="h-11 inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 text-sm font-semibold hover:bg-white/20 disabled:opacity-50"
          >
            {deshaciendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
            Deshacer
          </button>
        </div>
      )}
    </div>
  );
};

export default AbiertosTab;
